import posthog, { DisplaySurveyType } from 'posthog-js';

import { getEnvConfig } from 'utils/getEnvConfig';
import { probeWebGLSupportWhenIdle } from 'utils/webglSupport';
import { CurrentAccount } from 'types/CurrentAccount';
import { CurrentUser } from 'types/CurrentUser';

type License = 'Viewer' | 'Analyst' | 'Standard';

const getLicense = (currentUser: CurrentUser): License => {
  if (currentUser.viewer) return 'Viewer';
  if (currentUser.analyst) return 'Analyst';
  return 'Standard';
};

export const identifyUser = (
  currentAccount: CurrentAccount,
  currentUser: CurrentUser
) => {
  const isAnonymized = currentAccount.subProcessors.posthog.anonymized;

  posthog.identify(`${currentAccount.id}-${currentUser.id}`, {
    // User
    email: isAnonymized ? null : currentUser.email,
    name_first: isAnonymized ? null : currentUser.firstName,
    name_last: isAnonymized ? null : currentUser.lastName,

    // License
    license: getLicense(currentUser),
    has_license_viewer: currentUser.viewer,
    has_license_analyst: currentUser.analyst,
    has_license_standard: !currentUser.viewer && !currentUser.analyst,

    // Roles
    has_role_super_admin: currentUser.superAdmin,
    has_role_program_lead: currentUser.programLead,
    has_role_coach: currentUser.coach,

    // Language
    language: currentUser.locale,
  });
};

export const identifyAccount = (currentAccount: CurrentAccount) => {
  posthog.group('account', currentAccount.id.toString(), {
    // Account
    name: currentAccount.name,
    subdomain: currentAccount.subdomain,

    // Commercial
    trial_end_date: currentAccount.trialEndDate,
    subscription_end_date: currentAccount.subscriptionEndDate,
    demo: currentAccount.demo,
    sst: currentAccount.sst,
    ct: currentAccount.ct,
    p: currentAccount.p,

    // Features
    has_feature_ai_companion: currentAccount.features.aiChat === 'active',
  });
};

export const initPostHog = (
  currentAccount: CurrentAccount,
  currentUser: CurrentUser
) => {
  if (!currentAccount?.subProcessors?.posthog?.enabled || !currentUser) return;

  posthog.init(getEnvConfig('POSTHOG_KEY'), {
    api_host: getEnvConfig('POSTHOG_HOST'),
    defaults: '2025-05-24',
    person_profiles: 'identified_only',
    loaded: () => {
      identifyUser(currentAccount, currentUser);
      identifyAccount(currentAccount);
      // Sent with every later event, so usage can be broken down by graphics support.
      probeWebGLSupportWhenIdle(support =>
        posthog.register({ webgl_support: support })
      );
    },

    // Disable unused features
    disable_session_recording: true,
    enable_recording_console_log: false,
    mask_all_text: true,
  });
};

export const isPostHogAvailable = (currentAccount: CurrentAccount): boolean => {
  return !!(
    // eslint-disable-next-line no-underscore-dangle
    (currentAccount?.subProcessors?.posthog?.enabled && posthog.__loaded)
  );
};

// PostHog Feedback Survey IDs
const FEEDBACK_SURVEY_ID_DE = '0199e16a-5557-0000-1631-7ef5fed2237f';
const FEEDBACK_SURVEY_ID_EN = '0199e148-9744-0000-c484-7612e16c2115';

export const launchFeedbackSurvey = (locale: string) => {
  const surveyId =
    locale === 'de' ? FEEDBACK_SURVEY_ID_DE : FEEDBACK_SURVEY_ID_EN;

  posthog.getActiveMatchingSurveys(surveys => {
    const survey = surveys.find(s => s.id === surveyId);
    if (survey) {
      posthog.displaySurvey(surveyId, {
        displayType: DisplaySurveyType.Popover,
        ignoreConditions: true,
        ignoreDelay: true,
      });
    }
  });
};
